import { AllocationUsage } from '@/components/Timeline/allocation/types';
import { getDurationInDays } from '@/components/Timeline/logic/durations';
import {
    AllocationAreaPhaseTileDataDocument,
    AllocationRoomPhaseTileDataDocument,
    ExternalEventAllocationPhaseTileDataDocument,
} from '@/generated/graphql';
import { useQuery } from '@vue/apollo-composable';
import { DateTime } from 'luxon';
import { computed, Ref } from 'vue';

const HIGHLIGHTED_PHASES = ['DURCH', 'RAUF', 'RABB', 'E-DURCH', 'P-DURCH'];

function getTilePhaseForm(position: number, maxPosition: number) {
    if (position === 0) {
        return 'Left-End' as const;
    }
    if (position === maxPosition) {
        return 'Right-End' as const;
    }
    return 'Middle' as const;
}

export function usePhaseTileData(
    queryDocument:
        | typeof AllocationAreaPhaseTileDataDocument
        | typeof AllocationRoomPhaseTileDataDocument
        | typeof ExternalEventAllocationPhaseTileDataDocument,
    phaseTypes: AllocationUsage[],
    referenceAllocationId: Ref<string | undefined>,
) {
    const { result, loading } = useQuery(
        queryDocument,
        () => ({ id: referenceAllocationId.value! }),
        () => ({
            enabled: !!referenceAllocationId.value,
        }),
    );

    const referenceAllocation = computed(() => result.value?.allocation);

    const durationInDays = computed(() => {
        if (referenceAllocation.value) {
            const endDateTime = DateTime.fromISO(referenceAllocation.value.end);
            const startDateTime = DateTime.fromISO(referenceAllocation.value.start);
            return endDateTime.diff(startDateTime, 'days').days;
        }
        return 0;
    });

    const phaseTilePhases = computed(() => {
        if (referenceAllocation.value) {
            const allocationStart = DateTime.fromISO(referenceAllocation.value.start);
            const mappedPhases = [];
            const phases = referenceAllocation.value.phases.nodes;
            for (const phaseType of phaseTypes) {
                const phase = phases.find((p) => p.usage.id === phaseType.id);
                const phaseColor = phaseType.color;
                const phasePosition = phaseType.position;
                if (phase) {
                    const offsetDays = getDurationInDays(phase.offset);
                    const durationDays = getDurationInDays(phase.duration);
                    mappedPhases.push({
                        labelShort: phase.usage.nameShort,
                        labelLong: phase.usage.name,
                        isHighlighted: HIGHLIGHTED_PHASES.includes(phase.usage.nameShort),
                        form: getTilePhaseForm(phasePosition, phaseTypes.length - 1),
                        phaseStart: allocationStart.plus({ days: offsetDays }),
                        phaseEnd: allocationStart.plus({ days: offsetDays + durationDays }),
                        phaseType: phase.usage.type,
                        phaseColor: phaseColor,
                    });
                } else {
                    mappedPhases.push({
                        labelShort: phaseType.nameShort,
                        labelLong: phaseType.name,
                        isHighlighted: false,
                        form: getTilePhaseForm(phasePosition, phaseTypes.length - 1),
                        phaseStart: null,
                        phaseEnd: null,
                        phaseType: phaseType.type,
                        phaseColor: phaseColor,
                    });
                }
            }
            return mappedPhases;
        }
        return [];
    });

    const showPhasesTile = computed(() => Boolean(!loading.value && referenceAllocation.value));
    const hasNoReferenceAllocation = computed(() => Boolean(!loading.value && !referenceAllocation.value));

    const phaseTileStatus = computed(() => {
        if (loading.value) {
            return 'loading';
        }
        if (!loading.value && referenceAllocation.value) {
            return 'loaded';
        }
        if (!loading.value && !referenceAllocation.value) {
            return 'no-data';
        }
    });

    return {
        loading,
        durationInDays,
        phaseTilePhases,
        showPhasesTile,
        hasNoReferenceAllocation,
        phaseTileStatus,
    };
}
